import { inject, Injectable } from '@angular/core';
import { getHttpErrorCode } from '../http-error.util';
import { MonitoringService } from './monitoring.service';
import {
  AuthLoginFailedPayload,
  AuthLoginMethod,
  AuthObservabilityEvents,
} from './models/auth-observability.model';

@Injectable({
  providedIn: 'root',
})
export class AuthObservabilityService {
  private readonly monitoring = inject(MonitoringService);

  trackLoginSucceeded(method: AuthLoginMethod): void {
    this.monitoring.trackEvent(AuthObservabilityEvents.LOGIN_SUCCEEDED, { method });
  }

  trackLoginFailed(payload: AuthLoginFailedPayload): void {
    this.monitoring.trackEvent(AuthObservabilityEvents.LOGIN_FAILED, {
      method: payload.method,
      errorCode: payload.errorCode,
    });
  }

  trackLoginFailedFromError(method: AuthLoginMethod, error: unknown): void {
    this.trackLoginFailed({ method, errorCode: this.resolveErrorCode(error) });
  }

  trackBiometricUnlinked(): void {
    this.monitoring.trackEvent(AuthObservabilityEvents.BIOMETRIC_UNLINKED);
  }

  trackBiometricUnlinkFailed(error: unknown): void {
    this.monitoring.trackEvent(AuthObservabilityEvents.BIOMETRIC_UNLINK_FAILED, {
      errorCode: this.resolveErrorCode(error),
    });
    this.monitoring.captureException(error instanceof Error ? error : new Error(String(error)));
  }

  trackTokenRefreshed(): void {
    this.monitoring.trackEvent(AuthObservabilityEvents.TOKEN_REFRESHED);
  }

  trackAccessDenied(): void {
    this.monitoring.trackEvent(AuthObservabilityEvents.ACCESS_DENIED);
  }

  private resolveErrorCode(error: unknown): string {
    if (error && typeof error === 'object' && 'error' in error) {
      const httpError = error as { error?: unknown };
      return getHttpErrorCode(httpError.error) || getHttpErrorCode(httpError);
    }
    return getHttpErrorCode(error);
  }
}
